import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './FichierPro.css';

import { useRafraichissementAuto } from '../rafraichissementAuto';
const API_URL = process.env.REACT_APP_API_URL || '';

const statutLabels = {
  nouveau: 'Nouveau',
  contacte: 'Contacté',
  rappel: 'À rappeler',
  interesse: 'Intéressé',
  rdv_pris: 'RDV pris',
  negociation: 'Négociation',
  converti: 'Converti',
  pas_interesse: 'Pas intéressé',
  nrp: 'NRP',
  faux_num: 'Faux numéro',
  perdu: 'Perdu'
};

function isImage(url) {
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(url || '');
}
function isPdf(url) {
  return /\.pdf$/i.test(url || '');
}

// Libellés + ordre d'affichage de la fiche CEE structurée
const FICHE_LABELS = {
  raison_sociale: 'Raison sociale', siret: 'SIRET', gerant: 'Gérant',
  adresse: 'Adresse', code_postal: 'Code postal', ville: 'Ville',
  tel_fixe: 'Téléphone fixe', tel_mobile: 'Téléphone mobile', secteur: "Secteur d'activité",
  individuel: 'En individuel', jour_repos: 'Jour de fermeture/repos', proprietaire: 'Propriétaire du fonds',
  etude_batterie: 'Étude batterie condensateur', presence_batterie: 'Présence batterie condensateur',
  age_activite: "Âge d'activité", facture_elec: "Facture d'électricité", frequence_paiement: 'Fréquence de paiement',
  eclairage_leds: 'Éclairage LED', commentaire: 'Commentaire', rdv: 'Rendez-vous', telepro: 'Télépro'
};
const FICHE_ORDER = ['rdv', 'telepro', 'raison_sociale', 'siret', 'gerant', 'proprietaire', 'adresse', 'code_postal', 'ville', 'tel_fixe', 'tel_mobile', 'secteur', 'individuel', 'jour_repos', 'age_activite', 'facture_elec', 'frequence_paiement', 'etude_batterie', 'presence_batterie', 'eclairage_leds', 'commentaire'];

function parseFiche(raw) {
  if (!raw) return null;
  try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch (e) { return null; }
}

const TRAITEURS_CEE = [168, 169]; // Maréchal (169) & Bouquet (168) : traitent tout le pool CEE

function LeadsCEEAgent({ currentUser, leadType = 'cee' } = {}) {
  const poleLabel = leadType === 'pacpv' ? 'PAC/PV' : 'CEE';
  const isChef = currentUser?.role === 'chef_equipe';
  const isTraiteur = TRAITEURS_CEE.includes(Number(currentUser?.id));
  const isAgentCee = currentUser?.role === 'agent' && !!currentUser?.is_agent_cee;
  // Qui peut modifier statut/commentaire d'un lead : le chef, ou l'agent à qui le lead est attribué.
  // Un agent (créateur ou traiteur) ne modifie QUE les leads qui lui sont attribués ; les autres = suivi lecture seule.
  const canEditLead = (l) => isChef || (l.agent_id != null && Number(l.agent_id) === Number(currentUser?.id));
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatut, setFilterStatut] = useState('tous');
  const [filterActivite, setFilterActivite] = useState('tous');
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState(null); // { url, nom }
  const [fiche, setFiche] = useState(null); // { data, nom, fichier_url }
  const [detail, setDetail] = useState(null); // lead complet affiché dans "Voir fiche"
  const [detailComments, setDetailComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [team, setTeam] = useState([]); // membres de l'équipe (mode chef d'équipe)
  const [commentEdit, setCommentEdit] = useState(null); // { id, nom, value, readOnly }
  const [savingComment, setSavingComment] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const emptyForm = { nom: '', entreprise: '', telephone: '', email: '', message: '', file: null };
  const [createForm, setCreateForm] = useState(emptyForm);

  const fetchLeads = async (silencieux = false) => {
    if (!silencieux) setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ type: leadType });
      if (filterStatut && filterStatut !== 'tous') params.append('statut', filterStatut);
      if (search) params.append('search', search);
      const res = await axios.get(`${API_URL}/api/leads?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors du chargement des leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeam = async () => {
    if (!isChef || !currentUser?.id) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/chefs-equipe/${currentUser.id}/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeam(res.data || []);
    } catch (err) {
      // équipe vide ou erreur : on laisse la liste vide
    }
  };

  // Mise a jour automatique de la page (voir src/rafraichissementAuto.js)
  useRafraichissementAuto(() => { fetchLeads(true) }, { secondes: 30 });

  useEffect(() => {
    fetchLeads();
    fetchTeam();
    // eslint-disable-next-line
  }, []);

  const handleAssign = async (id, agent_id) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(`${API_URL}/api/leads/${id}/assigner`, { agent_id: agent_id || null }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(prev => prev.map(l => l.id === id ? { ...l, agent_id: res.data.agent_id, agent_nom: res.data.agent_nom } : l));
    } catch (err) {
      alert(err.response?.data?.error || "Erreur lors de l'attribution");
    }
  };

  const handleStatutChange = async (id, statut) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(`${API_URL}/api/leads/${id}/statut`, { statut }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(prev => prev.map(l => l.id === id ? { ...l, statut: res.data.statut, agent_id: res.data.agent_id, agent_nom: res.data.agent_nom } : l));
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur mise à jour statut');
    }
  };

  const saveComment = async () => {
    if (!commentEdit) return;
    setSavingComment(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(`${API_URL}/api/leads/${commentEdit.id}/notes`, { notes: commentEdit.value }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(prev => prev.map(l => l.id === commentEdit.id ? { ...l, notes: res.data.notes } : l));
      setCommentEdit(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'enregistrement du commentaire');
    } finally {
      setSavingComment(false);
    }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!createForm.nom.trim() && !createForm.entreprise.trim()) {
      alert('Renseignez au moins le nom ou l\'entreprise.');
      return;
    }
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('nom', createForm.nom.trim() || createForm.entreprise.trim());
      fd.append('entreprise', createForm.entreprise.trim());
      fd.append('telephone', createForm.telephone.trim());
      fd.append('email', createForm.email.trim());
      fd.append('message', createForm.message.trim());
      fd.append('type', leadType);
      if (createForm.file) fd.append('fichier', createForm.file);
      const res = await axios.post(`${API_URL}/api/leads-cee`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setLeads(prev => [res.data, ...prev]);
      setCreateForm(emptyForm);
      setCreateOpen(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la création du lead');
    } finally {
      setCreating(false);
    }
  };

  const openFile = (lead) => {
    const url = `${API_URL}${lead.fichier_url}`;
    if (isImage(lead.fichier_url) || isPdf(lead.fichier_url)) {
      setPreview({ url, nom: lead.fichier_nom || 'Document', pdf: isPdf(lead.fichier_url) });
    } else {
      window.open(url, '_blank', 'noopener');
    }
  };

  // Qui peut ajouter un commentaire : chef, agent assigné, ou créateur du lead
  const canComment = (l) => canEditLead(l) || (l.created_by != null && Number(l.created_by) === Number(currentUser?.id));

  // Ouvre la fiche complète et charge l'historique des commentaires
  const openDetail = async (lead) => {
    setDetail(lead);
    setNewComment('');
    setDetailComments([]);
    setLoadingComments(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/leads/${lead.id}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDetailComments(res.data || []);
    } catch (err) {
      setDetailComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  const addComment = async () => {
    if (!detail || !newComment.trim()) return;
    setAddingComment(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/leads/${detail.id}/comments`, { comment: newComment.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDetailComments(res.data.comments || []);
      setNewComment('');
      // refléter le dernier commentaire dans la liste (colonne aperçu)
      setLeads(prev => prev.map(l => l.id === detail.id ? { ...l, notes: res.data.notes } : l));
    } catch (err) {
      alert(err.response?.data?.error || "Erreur lors de l'ajout du commentaire");
    } finally {
      setAddingComment(false);
    }
  };

  const activites = [...new Set(leads.map(l => l.activite).filter(Boolean))].sort();
  const displayed = filterActivite === 'tous' ? leads : leads.filter(l => l.activite === filterActivite);

  return (
    <div className="fichier-pro">
      <div className="fichier-pro-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h2>Leads {poleLabel}</h2>
          <p className="fichier-pro-subtitle">
            {isChef ? `Leads ${poleLabel} de votre équipe` : isTraiteur ? `Vos leads ${poleLabel} attribués à traiter` : `Vos leads ${poleLabel} (créés et suivis)`} | <strong>{leads.length}</strong> lead{leads.length !== 1 ? 's' : ''}
          </p>
        </div>
        {(isAgentCee || isChef) && (
          <button className="btn-search" onClick={() => { setCreateForm(emptyForm); setCreateOpen(true); }} style={{ whiteSpace: 'nowrap' }}>
            ＋ Nouveau lead
          </button>
        )}
      </div>

      <div className="fichier-pro-filters">
        <div className="filter-group">
          <label>Statut</label>
          <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
            <option value="tous">-- Tous --</option>
            {Object.entries(statutLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Activité</label>
          <select value={filterActivite} onChange={e => setFilterActivite(e.target.value)}>
            <option value="tous">-- Toutes --</option>
            {activites.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Recherche libre</label>
          <input
            type="text"
            className="filter-input"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Nom, entreprise, tél, email..."
            onKeyDown={e => e.key === 'Enter' && fetchLeads()}
          />
        </div>
        <button className="btn-search" onClick={() => fetchLeads()} disabled={loading}>
          {loading ? <><span className="spinner-small"></span> Chargement...</> : 'Actualiser'}
        </button>
      </div>

      {error && <div className="fichier-pro-error">{error}</div>}

      {!loading && (
        <div className="fichier-pro-results">
          <div className="results-header">
            <span className="results-count">{displayed.length} lead{displayed.length !== 1 ? 's' : ''}{filterActivite !== 'tous' ? ` · ${filterActivite}` : ''}</span>
          </div>
          {displayed.length > 0 ? (
            <div className="table-wrapper">
              <table className="fichier-pro-table lead-table-compact">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Entreprise</th>
                    <th>Téléphone</th>
                    <th>Email</th>
                    <th>Activité</th>
                    <th>Suivi</th>
                    <th>Fiche / Document</th>
                    <th>Commentaire</th>
                    {isChef && <th>Attribuer à</th>}
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {displayed.map(l => (
                    <tr key={l.id}>
                      <td className="td-nom">{l.nom}{l.message ? <><br/><small style={{color:'#888'}}>{l.message}</small></> : ''}</td>
                      <td className="col-entreprise" title={l.entreprise || ''}>{l.entreprise || '-'}</td>
                      <td className="td-phone">{l.telephone ? <a href={`tel:${l.telephone}`} className="phone-link">{l.telephone}</a> : '-'}</td>
                      <td className="col-email" title={l.email || ''}>{l.email ? <a href={`mailto:${l.email}`} className="web-link">{l.email}</a> : '-'}</td>
                      <td>{l.activite ? <span style={{ display: 'inline-block', padding: '3px 9px', borderRadius: '12px', background: '#eef2ff', color: '#4338ca', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>{l.activite}</span> : <span style={{ color: '#bbb' }}>—</span>}</td>
                      <td className="col-suivi" style={{ fontSize: '11px', lineHeight: 1.35 }}>
                        <div><span style={{ color: '#888' }}>Créé:</span> {l.createur_nom || '—'}</div>
                        <div><span style={{ color: '#888' }}>Traité:</span> {l.agent_nom ? <strong style={{ color: '#0a8a4a' }}>{l.agent_nom}</strong> : <em style={{ color: '#c47b00' }}>Pool</em>}</div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          <button className="btn-voir-fiche" style={{ background: '#2563eb', color: '#fff' }}
                            onClick={() => openDetail(l)}>
                            👁 Voir fiche
                          </button>
                          {parseFiche(l.fiche_data) && (
                            <button className="btn-voir-fiche" style={{ background: '#7b2ff7', color: '#fff' }}
                              onClick={() => setFiche({ data: parseFiche(l.fiche_data), nom: l.nom, fichier_url: l.fichier_url, fichier_nom: l.fichier_nom })}>
                              📋 Fiche
                            </button>
                          )}
                          {l.fichier_url && (
                            <button className="btn-voir-fiche" onClick={() => openFile(l)} title={l.fichier_nom || 'Voir le document'}>
                              {isPdf(l.fichier_url) ? '📄 PDF' : isImage(l.fichier_url) ? '🖼️ Photo' : '📎 Doc'}
                            </button>
                          )}
                        </div>
                      </td>
                      <td>
                        {canEditLead(l) ? (
                          <button
                            className="btn-voir-fiche"
                            style={{ background: l.notes ? '#0a8a4a' : '#6b7280' }}
                            title={l.notes || 'Ajouter un commentaire'}
                            onClick={() => setCommentEdit({ id: l.id, nom: l.nom, value: l.notes || '', readOnly: false })}
                          >
                            💬 {l.notes ? 'Voir / Éditer' : 'Ajouter'}
                          </button>
                        ) : l.notes ? (
                          <button
                            className="btn-voir-fiche"
                            style={{ background: '#6b7280' }}
                            title={l.notes}
                            onClick={() => setCommentEdit({ id: l.id, nom: l.nom, value: l.notes || '', readOnly: true })}
                          >
                            💬 Voir
                          </button>
                        ) : <span style={{ color: '#bbb' }}>—</span>}
                        {l.notes && (
                          <div style={{ color: '#666', fontSize: '11px', marginTop: '3px', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {l.notes.length > 40 ? l.notes.slice(0, 40) + '…' : l.notes}
                          </div>
                        )}
                      </td>
                      {isChef && (
                        <td>
                          <select
                            className="statut-select"
                            value={l.agent_id || ''}
                            onChange={e => handleAssign(l.id, e.target.value)}
                          >
                            <option value="">— Pool (non attribué) —</option>
                            <option value={currentUser.id}>Moi</option>
                            {team.map(m => (
                              <option key={m.id} value={m.id}>{m.nom || m.email}</option>
                            ))}
                          </select>
                        </td>
                      )}
                      <td className="td-statut">
                        {canEditLead(l) ? (
                          <select
                            className={`statut-select statut-${l.statut || 'nouveau'}`}
                            value={l.statut || 'nouveau'}
                            onChange={e => handleStatutChange(l.id, e.target.value)}
                          >
                            {Object.entries(statutLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                        ) : (
                          <span className={`statut-select statut-${l.statut || 'nouveau'}`} style={{ display: 'inline-block', cursor: 'default' }} title="Lecture seule — traité par l'équipe CEE">
                            {statutLabels[l.statut] || statutLabels.nouveau}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-results">Aucun lead {poleLabel} pour le moment.</div>
          )}
        </div>
      )}

      {/* Création d'un lead CEE par l'agent */}
      {createOpen && (
        <div className="modal-overlay" onClick={() => !creating && setCreateOpen(false)}>
          <div className="modal-content planning-modal" style={{ maxWidth: '560px', width: '92vw' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>＋ Nouveau lead {poleLabel}</h3>
              <button className="btn-close" onClick={() => !creating && setCreateOpen(false)}>&times;</button>
            </div>
            <form onSubmit={submitCreate} style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <p style={{ margin: 0, color: '#666', fontSize: '13px' }}>
                {leadType === 'cee'
                  ? 'Le lead est attribué automatiquement à un traiteur CEE (Maréchal ou Bouquet, en alternance). Vous gardez le suivi de votre lead.'
                  : 'Le lead vous est attribué (ou au pool de votre équipe). Votre chef d\'équipe peut le réattribuer.'}
              </p>
              <div className="filter-group" style={{ margin: 0 }}>
                <label>Nom / Raison sociale</label>
                <input type="text" className="filter-input" value={createForm.nom}
                  onChange={e => setCreateForm({ ...createForm, nom: e.target.value })} placeholder="Nom du contact ou de l'entreprise" autoFocus />
              </div>
              <div className="filter-group" style={{ margin: 0 }}>
                <label>Entreprise</label>
                <input type="text" className="filter-input" value={createForm.entreprise}
                  onChange={e => setCreateForm({ ...createForm, entreprise: e.target.value })} placeholder="Entreprise" />
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <div className="filter-group" style={{ margin: 0, flex: 1, minWidth: '180px' }}>
                  <label>Téléphone</label>
                  <input type="text" className="filter-input" value={createForm.telephone}
                    onChange={e => setCreateForm({ ...createForm, telephone: e.target.value })} placeholder="06 / 07 / fixe" />
                </div>
                <div className="filter-group" style={{ margin: 0, flex: 1, minWidth: '180px' }}>
                  <label>Email</label>
                  <input type="email" className="filter-input" value={createForm.email}
                    onChange={e => setCreateForm({ ...createForm, email: e.target.value })} placeholder="email@exemple.fr" />
                </div>
              </div>
              <div className="filter-group" style={{ margin: 0 }}>
                <label>Commentaire / Note</label>
                <textarea value={createForm.message}
                  onChange={e => setCreateForm({ ...createForm, message: e.target.value })}
                  placeholder="Infos utiles, secteur, contexte de prospection..."
                  style={{ width: '100%', minHeight: '80px', padding: '10px', borderRadius: '8px', border: '1px solid #ccc', fontFamily: 'inherit', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
              <div className="filter-group" style={{ margin: 0 }}>
                <label>Fiche / Photo / PDF (optionnel)</label>
                <input type="file" accept="image/*,.pdf,.doc,.docx" onChange={e => setCreateForm({ ...createForm, file: e.target.files[0] || null })} />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn-close" style={{ position: 'static', fontSize: '14px', padding: '8px 16px' }} onClick={() => setCreateOpen(false)} disabled={creating}>Annuler</button>
                <button type="submit" className="btn-search" disabled={creating}>
                  {creating ? <><span className="spinner-small"></span> Création...</> : 'Créer le lead'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Édition du commentaire */}
      {commentEdit && (
        <div className="modal-overlay" onClick={() => !savingComment && setCommentEdit(null)}>
          <div className="modal-content planning-modal" style={{ maxWidth: '520px', width: '92vw' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>💬 Commentaire — {commentEdit.nom}</h3>
              <button className="btn-close" onClick={() => !savingComment && setCommentEdit(null)}>&times;</button>
            </div>
            <div style={{ padding: '14px' }}>
              {commentEdit.readOnly && <p style={{ margin: '0 0 8px', color: '#c47b00', fontSize: '13px' }}>🔒 Lecture seule — ce lead est traité par l'équipe CEE.</p>}
              <textarea
                value={commentEdit.value}
                onChange={e => setCommentEdit({ ...commentEdit, value: e.target.value })}
                placeholder="Saisissez votre commentaire sur ce lead..."
                autoFocus={!commentEdit.readOnly}
                readOnly={commentEdit.readOnly}
                style={{ width: '100%', minHeight: '140px', padding: '10px', borderRadius: '8px', border: '1px solid #ccc', fontFamily: 'inherit', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box', background: commentEdit.readOnly ? '#f5f5f7' : '#fff' }}
              />
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '12px' }}>
                <button className="btn-close" style={{ position: 'static', fontSize: '14px', padding: '8px 16px' }} onClick={() => setCommentEdit(null)} disabled={savingComment}>{commentEdit.readOnly ? 'Fermer' : 'Annuler'}</button>
                {!commentEdit.readOnly && (
                  <button className="btn-search" onClick={saveComment} disabled={savingComment}>
                    {savingComment ? <><span className="spinner-small"></span> Enregistrement...</> : 'Enregistrer'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fiche complète : toutes les infos + tout l'historique des commentaires */}
      {detail && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal-content planning-modal" style={{ maxWidth: '720px', width: '94vw', maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>👁 Fiche lead — {detail.nom}</h3>
              <button className="btn-close" onClick={() => setDetail(null)}>&times;</button>
            </div>
            <div style={{ padding: '14px', color: '#1f2937' }}>
              {/* Informations */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '8px 18px', fontSize: '14px', color: '#1f2937' }}>
                <div><strong>Entreprise :</strong> {detail.entreprise || '—'}</div>
                <div><strong>Activité :</strong> {detail.activite || '—'}</div>
                <div><strong>Téléphone :</strong> {detail.telephone ? <a href={`tel:${detail.telephone}`} className="phone-link">{detail.telephone}</a> : '—'}</div>
                <div><strong>Email :</strong> {detail.email ? <a href={`mailto:${detail.email}`} className="web-link">{detail.email}</a> : '—'}</div>
                <div><strong>Statut :</strong> {statutLabels[detail.statut] || detail.statut || '—'}</div>
                <div><strong>Créé par :</strong> {detail.createur_nom || '—'}</div>
                <div><strong>Traité par :</strong> {detail.agent_nom || 'Pool'}</div>
                <div><strong>Date :</strong> {detail.created_at ? new Date(detail.created_at).toLocaleString('fr-FR') : '—'}</div>
              </div>

              {/* Note de création */}
              {detail.message && (
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontWeight: 600, color: '#555', marginBottom: '4px' }}>Note de création</div>
                  <div style={{ background: '#f7f7fa', border: '1px solid #e3e3ef', borderRadius: '8px', padding: '10px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '14px' }}>{detail.message}</div>
                </div>
              )}

              {/* Boutons fiche structurée / document */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
                {parseFiche(detail.fiche_data) && (
                  <button className="btn-voir-fiche" style={{ background: '#7b2ff7', color: '#fff' }}
                    onClick={() => setFiche({ data: parseFiche(detail.fiche_data), nom: detail.nom, fichier_url: detail.fichier_url, fichier_nom: detail.fichier_nom })}>
                    📋 Fiche CEE détaillée
                  </button>
                )}
                {detail.fichier_url && (
                  <button className="btn-voir-fiche" onClick={() => openFile(detail)}>
                    {isPdf(detail.fichier_url) ? '📄 PDF' : isImage(detail.fichier_url) ? '🖼️ Photo' : '📎 Document'}
                  </button>
                )}
              </div>

              {/* Historique des commentaires */}
              <div style={{ marginTop: '16px' }}>
                <div style={{ fontWeight: 600, color: '#555', marginBottom: '6px' }}>💬 Commentaires ({detailComments.length})</div>
                {loadingComments ? (
                  <p style={{ color: '#888', fontSize: '13px' }}>Chargement…</p>
                ) : detailComments.length === 0 ? (
                  <p style={{ color: '#999', fontSize: '13px' }}>Aucun commentaire pour le moment.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '260px', overflowY: 'auto' }}>
                    {detailComments.map(c => (
                      <div key={c.id} style={{ border: '1px solid #e3e3ef', borderRadius: '8px', padding: '8px 10px', background: '#fff' }}>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '3px', display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                          <strong style={{ color: '#2563eb' }}>{c.auteur_nom || 'Anonyme'}</strong>
                          <span>{c.created_at ? new Date(c.created_at).toLocaleString('fr-FR') : ''}</span>
                        </div>
                        <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{c.comment}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Ajout d'un commentaire */}
                {canComment(detail) && (
                  <div style={{ marginTop: '10px' }}>
                    <textarea
                      value={newComment}
                      onChange={e => setNewComment(e.target.value)}
                      placeholder="Ajouter un commentaire (n'écrase aucun commentaire existant)…"
                      style={{ width: '100%', minHeight: '70px', padding: '10px', borderRadius: '8px', border: '1px solid #ccc', fontFamily: 'inherit', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                      <button className="btn-search" onClick={addComment} disabled={addingComment || !newComment.trim()}>
                        {addingComment ? <><span className="spinner-small"></span> Ajout…</> : 'Ajouter le commentaire'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fiche CEE structurée (tout dans le CRM) */}
      {fiche && (
        <div className="modal-overlay" onClick={() => setFiche(null)}>
          <div className="modal-content planning-modal" style={{ maxWidth: '720px', width: '92vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>📋 Fiche CEE — {fiche.nom}</h3>
              <button className="btn-close" onClick={() => setFiche(null)}>&times;</button>
            </div>
            <div className="fiche-detail-body" style={{ padding: '6px 4px' }}>
              {FICHE_ORDER.filter(k => fiche.data[k]).map(k => (
                <div className="fiche-detail-row" key={k}>
                  <span className="fiche-label">{FICHE_LABELS[k] || k}</span>
                  <span className="fiche-value">
                    {k === 'tel_fixe' || k === 'tel_mobile'
                      ? <a href={`tel:${fiche.data[k]}`} className="phone-link">{fiche.data[k]}</a>
                      : fiche.data[k]}
                  </span>
                </div>
              ))}
              {fiche.data.texte_complet && (
                <div style={{ marginTop: '14px' }}>
                  <div className="fiche-label" style={{ marginBottom: '6px' }}>Contenu complet du document</div>
                  <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', fontSize: '13px', lineHeight: 1.5, background: '#f7f7fa', border: '1px solid #e3e3ef', borderRadius: '8px', padding: '12px', margin: 0, color: '#333' }}>
                    {fiche.data.texte_complet}
                  </pre>
                </div>
              )}
              {fiche.fichier_url && (
                <div className="fiche-detail-actions" style={{ marginTop: '12px' }}>
                  <a className="btn-search" style={{ textDecoration: 'none', display: 'inline-block' }}
                     href={`${API_URL}${fiche.fichier_url}`} target="_blank" rel="noopener noreferrer">
                    Ouvrir le document d'origine
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Aperçu photo / PDF */}
      {preview && (
        <div className="modal-overlay" onClick={() => setPreview(null)}>
          <div className="modal-content" style={{ maxWidth: '90vw', width: 'auto', maxHeight: '92vh' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>{preview.nom}</h3>
              <button className="btn-close" onClick={() => setPreview(null)}>&times;</button>
            </div>
            <div style={{ padding: '10px', textAlign: 'center' }}>
              {preview.pdf ? (
                <iframe title="document" src={preview.url} style={{ width: '80vw', height: '78vh', border: 'none' }} />
              ) : (
                <img src={preview.url} alt={preview.nom} style={{ maxWidth: '88vw', maxHeight: '78vh', objectFit: 'contain' }} />
              )}
              <div style={{ marginTop: '10px' }}>
                <a href={preview.url} target="_blank" rel="noopener noreferrer" className="btn-search" style={{ textDecoration: 'none', display: 'inline-block' }}>
                  Ouvrir dans un nouvel onglet
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeadsCEEAgent;
